/**
 * FlyTo animations — camera transitions to locations using the location index
 */
import { findLocation, CONTINENT_VIEWS } from '../utils/geo-utils.js';

class Animator {
  constructor(globe, locationIndex) {
    this.globe = globe;
    this.locationIndex = locationIndex;
  }

  /**
   * Update the location index (when new data is loaded)
   */
  setLocationIndex(index) {
    this.locationIndex = index;
  }

  /**
   * Fly to a location by name, ISO code, etc.
   * @returns {object|null} The location entry if found, with matchType
   */
  flyToLocation(query) {
    const loc = findLocation(this.locationIndex, query);
    if (!loc) {
      console.warn(`[Animator] Location not found in index: ${query}`);
      return null;
    }

    console.log(`[Animator] Found "${query}" → "${loc.name}" (${loc.dataset}, ${loc.matchType})`);
    this.globe.flyTo(loc.centroid[0], loc.centroid[1], loc.zoom);
    return loc;
  }

  /**
   * Fly to explicit coordinates (fallback when location index has no match)
   */
  flyToCoordinates(longitude, latitude, zoom = 8) {
    console.log(`[Animator] Flying to coordinates [${longitude}, ${latitude}]`);
    this.globe.flyTo(longitude, latitude, zoom);
  }

  /**
   * Fly to a country by ISO code
   */
  flyToCountry(isoCode) {
    return this.flyToLocation(isoCode);
  }

  /**
   * Fly to a state/province
   */
  flyToState(stateName, countryIso) {
    // Try specific "state, country" first
    if (countryIso) {
      const countryLoc = findLocation(this.locationIndex, countryIso);
      const countryName = countryLoc?.name;
      if (countryName) {
        const loc = findLocation(this.locationIndex, `${stateName}, ${countryName}`);
        if (loc) {
          this.globe.flyTo(loc.centroid[0], loc.centroid[1], loc.zoom);
          return loc;
        }
      }
    }
    return this.flyToLocation(stateName);
  }

  /**
   * Fly to a city
   */
  flyToCity(cityName) {
    return this.flyToLocation(cityName);
  }

  /**
   * Fly to a continent or region
   */
  flyToRegion(regionName) {
    // Check continent views first
    const view = CONTINENT_VIEWS[regionName];
    if (view) {
      this.globe.flyTo(view.longitude, view.latitude, view.zoom);
      return { centroid: [view.longitude, view.latitude], zoom: view.zoom, name: regionName };
    }

    return this.flyToLocation(regionName);
  }

  /**
   * Reset to the full globe view
   */
  resetView() {
    this.globe.resetView();
  }
}

export { Animator };
export default Animator;
